import React from 'react';
import { createRoot } from 'react-dom/client';
import * as ReactDOMShim from 'react-dom';
// React 19 removed the legacy ReactDOM top-level APIs; some deps still call them:
// react-slick -> findDOMNode, react-confirm-alert -> render/unmountComponentAtNode.
// Minimal compat shim so those components render instead of throwing.
{
  const rd: any = (ReactDOMShim as any).default ?? ReactDOMShim;
  if (rd && typeof rd.findDOMNode !== 'function') {
    rd.findDOMNode = (node: any) => (node && node.nodeType ? node : null);
  }
  // One root per container, reused across re-renders — calling createRoot twice on the
  // same node warns and leaks the previous root. Weak so detached nodes stay collectable.
  const legacyRoots = new WeakMap<Element | DocumentFragment, any>();
  if (rd && typeof rd.render !== 'function') {
    rd.render = (element: any, container: Element) => {
      let root = legacyRoots.get(container);
      if (!root) {
        root = createRoot(container);
        legacyRoots.set(container, root);
      }
      root.render(element);
    };
  }
  if (rd && typeof rd.unmountComponentAtNode !== 'function') {
    rd.unmountComponentAtNode = (container: Element) => {
      const root = legacyRoots.get(container);
      if (!root) return false;
      // createRoot().unmount() throws if called while React is rendering; the legacy API
      // was sync-safe, and callers (react-confirm-alert) unmount from event handlers.
      Promise.resolve().then(() => root.unmount());
      legacyRoots.delete(container);
      return true;
    };
  }
}
import { Provider } from 'react-redux';
import { configureStore } from './store/configureStore';
import { ApolloProvider } from '@apollo/client/react';
import App from './App';
import { router } from './router';
import { initSentry } from './sentry';
import { installChunkReloadHandler } from './utils/lazyWithRetry';
import { apolloClient } from './apollo/client';
import { initAnalytics, trackPageView } from './analytics';

initSentry();

// Recover tabs still running a pre-deploy build, whose content-hashed chunks
// no longer exist on the server.
installChunkReloadHandler();

// Validate environment variables
const requiredEnvVars = ['VITE_API_ENDPOINT'];
requiredEnvVars.forEach((varName) => {
  if (!(window?.env?.[varName] || import.meta.env[varName])) {
    throw new Error(`Missing required environment variable: ${varName}`);
  }
});

export { BACKEND_URI } from "./config";

export const store = configureStore();

// Analytics. One subscriber, fanning out to every configured destination (GA4 and
// our own /api/analytics/events) so they cannot disagree about what a page view
// is — see src/analytics.
//
// Subscribed here rather than in a React hook because router.subscribe fires for
// every state transition including ones RootLayout redirects away from, and
// state.matches carries the route pattern a hook would have to reconstruct.
initAnalytics();

// The initial load: the router does not notify for the state it starts in.
trackPageView({
  location: { pathname: window.location.pathname, search: window.location.search },
  matches: router.state.matches,
});

router.subscribe(trackPageView);

// Debounced resize handler
let resizeTimeout: number | undefined;
const debouncedResize = () => {
  if (resizeTimeout !== undefined) {
    clearTimeout(resizeTimeout);
  }
  resizeTimeout = window.setTimeout(() => {
    store.dispatch({
      type: 'metadata/SET_SCREEN_RESIZE',
      screenSize: window.innerWidth,
    });
  }, 100);
};

window.addEventListener('resize', debouncedResize);

// Cleanup function
const cleanup = () => {
  window.removeEventListener('resize', debouncedResize);
};

// Expose store when run in Cypress
if (window.Cypress) {
  window.store = store;
  window.cleanupAnalytics = cleanup;
}

// Create root and render app
const container = document.getElementById('root');
if (!container) throw new Error('Failed to find the root element');


// Persist root across HMR reloads
let root: ReturnType<typeof createRoot>;

if (!(container as any)._reactRoot) {
  root = createRoot(container);
  (container as any)._reactRoot = root;
} else {
  root = (container as any)._reactRoot;
}

root.render(
  <React.StrictMode>
    <ApolloProvider client={apolloClient}>
      <Provider store={store}>
        <App />
      </Provider>
    </ApolloProvider>
  </React.StrictMode>
);

// Cleanup on unmount (for testing purposes)
if (import.meta.env.MODE === 'test') {
  cleanup();
}